import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Button } from 'react-bootstrap';
import { Form, Input, Row, Col } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Assets
import userprofile from '../../assets/img/logo.png';
import Shreejii from '../../assets/img/Shreejii.apk';

// Constants
const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL,
  appId: process.env.REACT_APP_API_ID,
};

const Profile = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm(); // Add form instance
  
  // State
  const [userData, setUserData] = useState({
    name: '',
    mobile: '',
    dob: '',
    refCode: '',
    totalBonus: 0,
    credit: 0,
  });
  
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user ID and device ID from localStorage
  const userId = localStorage.getItem("userid");
  const devId = localStorage.getItem("dev_id");

  // API Helper
  const apiClient = useCallback(async (endpoint, data) => {
    try {
      const response = await axios.post(
        `${API_CONFIG.baseURL}${endpoint}`,
        {
          app_id: API_CONFIG.appId,
          ...data,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }, []);

  // Load user profile data
  const loadUserProfile = useCallback(async () => {
    if (!userId || !devId) {
      console.warn("Missing user_id or dev_id");
      return;
    }

    try {
      setLoading(true);
      const data = await apiClient('/POM_get_user_profile', {
        user_id: userId,
        dev_id: devId,
      });

      if (data?.name) {
        setUserData(prev => ({
          ...prev,
          name: data.name,
          refCode: data.ref_code || data.referral_code || "",
          totalBonus: data.total_bonus || 0,
        }));
        
        // Set form values here
        form.setFieldsValue({
          name: data.name,
          dob: data.dob || '',
        });
      }
    } catch (error) {
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }, [userId, devId, apiClient, form]);

  // Load user credit data
  const loadUserCredit = useCallback(async () => {
    if (!userId || !devId) return;

    try {
      const data = await apiClient('/user-credit', {
        user_id: userId,
        dev_id: devId,
      });

      if (data) {
        setUserData(prev => ({
          ...prev,
          credit: data.credit || 0,
        }));
      }
    } catch (error) {
      console.error("Failed to load credit data:", error);
    }
  }, [userId, devId, apiClient]);

  // Initial data load
  useEffect(() => {
    // Redirect if not authenticated
    if (!userId || !devId) {
      navigate('/');
      return;
    }

    loadUserProfile();
    loadUserCredit();
  }, [userId, devId, navigate, loadUserProfile, loadUserCredit]);

  // Update username
  const updateUsername = async (values) => {
    const nameToUpdate = values.name || userData.name;
    
    if (!nameToUpdate.trim()) {
      toast.warning("Please enter a name");
      return;
    }

    try {
      setIsSubmitting(true);
      setLoading(true);

      const response = await apiClient('/user-profile-update', {
        name: nameToUpdate,
        dob: values.dob || "",
        user_id: userId,
      });

      if (response?.success === "1") {
        const updatedName = response?.data?.FullName || nameToUpdate;
        
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Username updated successfully: ${updatedName}`,
          timer: 2000,
          showConfirmButton: false,
        });

        // Update local state
        setUserData(prev => ({ ...prev, name: updatedName }));
        form.setFieldsValue({ name: updatedName });
        
        navigate('/');
      } else {
        toast.error(response?.message || 'Failed to update profile');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          'An error occurred while updating the username';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Copy referral code
  const copyReferralCode = async () => {
    if (!userData.refCode) {
      toast.warning("No referral code available");
      return;
    }

    try {
      await navigator.clipboard.writeText(userData.refCode);
      toast.success("Referral Code Copied!");
    } catch (error) {
      toast.error("Failed to copy referral code");
    }
  };

  // Handle form submission
  const handleFormSubmit = (values) => {
    updateUsername(values);
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="spinner-wrapper">
      <div className="spinner">
        <div className="mesh-loader">
          <div className="set-one">
            <div className="circle"></div>
            <div className="circle"></div>
          </div>
          <div className="set-two">
            <div className="circle"></div>
            <div className="circle"></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section id="profile">
        <Container fluid className="p-0">
          <div className="profilepage">
            <div className="profile">
              <img src={userprofile} className="img-fluid" alt="User Profile" />
            </div>
          </div>

          <div className="profiledetails">
            <div className="cardstyle">
              {/* Action Buttons */}
              <div className="d-flex justify-content-between mb-2 balancebonus">
                <div className="mt-2">
                  <Button className="btnbal bg-success">
                    BALANCE: {userData.credit || 0}
                  </Button>
                </div>
                <div className="mt-2">
                  <Button className="btnbal bg-success">
                    BONUS: {(userData.totalBonus || 0).toFixed(2)}
                  </Button>
                </div>
                <div className="mt-2">
                  <a
                    href={Shreejii}
                    className="btn btn-success"
                    download="Shreejii.apk"
                  >
                    <i className="bi bi-download me-1"></i>
                    Download
                  </a>
                </div>
              </div>

              {/* Referral Code */}
              <div className="mt-3">
                <label className="fw-bold mb-1" style={{ color: "black" }}>
                  Referral Code
                </label>
                <div className="d-flex">
                  <Input
                    value={userData.refCode}
                    readOnly
                    className="me-2"
                  />
                  <Button type="primary" onClick={copyReferralCode}>
                    <i className="bi bi-copy me-1"></i>
                    Copy
                  </Button>
                </div>
              </div>

              {/* Profile Form */}
              <Form
                form={form}
                name="profileForm"
                layout="vertical"
                onFinish={handleFormSubmit}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Form.Item
                      label="Name"
                      name="name"
                      rules={[
                        { required: true, message: 'Please enter your name!' },
                        { min: 2, message: 'Name must be at least 2 characters' },
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined className="site-form-item-icon" />}
                        placeholder="Enter your name"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      label="Date of Birth"
                      name="dob"
                    >
                      <Input
                        type="date"
                        placeholder="Select date of birth"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="submitbtn"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        block
                      >
                        {isSubmitting ? 'Updating...' : 'Update Profile'}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              <ToastContainer position="top-right" autoClose={3000} />
            </div>
          </div>
        </Container>
      </section>

      {loading && <LoadingSpinner />}
    </>
  );
};

export default Profile;